import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Divider,
  Autocomplete,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Send, History } from '@mui/icons-material';
import { tenantTokenService, tokenDistributionService } from '../../../services/skillcoinService';
import memberService from '../../../services/memberService';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api'; // Assuming api is imported for direct API calls

const TokenDisbursement = ({ tokenId, tokenSymbol, onDisbursement }) => {
  const { token, tenantId } = useAuth();

  const [formData, setFormData] = useState({
    amount: 10,
    reason: '',
    notes: '',
    recipients: [],
    recipientType: 'individual',
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [recentDisbursements, setRecentDisbursements] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [initialMembersLoaded, setInitialMembersLoaded] = useState(false);

  const [tokenData, setTokenData] = useState({
    _id: tokenId,
    symbol: tokenSymbol || 'MBC',
    name: 'MemberCoin',
  });

  const [apiError, setApiError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisbursementsLoading, setIsDisbursementsLoading] = useState(false);

  const fetchRecentDisbursements = useCallback(async () => {
    try {
      setIsDisbursementsLoading(true);
      setApiError(null);
      
      // First, get the disbursements data
      const response = await tokenDistributionService.getDisbursements();
      console.log('Disbursements response:', response);

      if (response && response.success && response.data) {
        const disbursementsData = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];

        // Create a simple function to handle member name fetching
        const fetchMemberName = async (memberId) => {
          try {
            // Direct API call instead of using memberService
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };
            
            // Make the API call directly
            const memberResponse = await api.get(`/members/${memberId}`, { headers });
            
            console.log(`Fetched member ${memberId}:`, memberResponse.data);
            
            if (memberResponse.data && memberResponse.data.data) {
              const member = memberResponse.data.data;
              return getFormattedMemberName(member);
            } else if (memberResponse.data) {
              const member = memberResponse.data;
              return getFormattedMemberName(member);
            }
            return 'Unknown Member';
          } catch (err) {
            console.error(`Error fetching member ${memberId}:`, err);
            return 'Unknown Member';
          }
        };
        
        // Helper function to format member name from different data structures
        const getFormattedMemberName = (member) => {
          if (member.userId && typeof member.userId === 'object') {
            const firstName = member.userId.firstName || '';
            const lastName = member.userId.lastName || '';
            return `${firstName} ${lastName}`.trim() || 'Unknown Member';
          } else if (member.firstName || member.lastName) {
            const firstName = member.firstName || '';
            const lastName = member.lastName || '';
            return `${firstName} ${lastName}`.trim();
          } else if (member.name) {
            return member.name;
          }
          return 'Unknown Member';
        };

        // Process each disbursement
        const formattedDisbursementsPromises = disbursementsData.map(async (item) => {
          // Convert amount to a proper number
          const amount = typeof item.amount === 'number' 
            ? item.amount 
            : typeof item.amount === 'string' 
              ? parseFloat(item.amount) || 0 
              : item.totalAmount || 0;
          
          // Get recipient name if we have memberId
          let recipientDisplay = '1 member';
          
          if (item.memberId) {
            recipientDisplay = await fetchMemberName(item.memberId);
            console.log(`Member name for ${item.memberId}:`, recipientDisplay);
          } else if (item.recipientCount && item.recipientCount > 1) {
            recipientDisplay = `${item.recipientCount} members`;
          } else if (item.recipientType === 'individual' && Array.isArray(item.recipients) && item.recipients.length > 0) {
            recipientDisplay = `${item.recipients.length} members`;
          } else if (item.recipientType === 'all') {
            recipientDisplay = 'All members';
          } else if (item.recipientType === 'active') {
            recipientDisplay = 'Active members';
          } else if (item.successful) {
            recipientDisplay = `${item.successful} members`;
          }
          
          // Use either reason or description field for the reason display
          const reason = item.reason || item.description || 'Not specified';
          
          return {
            _id: item._id || item.reference || `temp-${Date.now()}-${Math.random()}`,
            date: item.date || item.createdAt || new Date().toISOString(),
            amount: amount,
            recipientType: item.recipientType || 'individual',
            recipients: recipientDisplay,
            reason: reason,
            createdBy: item.createdBy?.name || 'Admin User',
            status: item.status || 'COMPLETED',
            memberId: item.memberId
          };
        });
        
        // Wait for all promises to resolve
        const formattedDisbursements = await Promise.all(formattedDisbursementsPromises);
        
        console.log('Formatted disbursements:', formattedDisbursements);
        setRecentDisbursements(formattedDisbursements);
      } else {
        console.warn('Unexpected disbursements response format:', response);
        setRecentDisbursements([]);
      }
    } catch (error) {
      console.error('Error fetching recent disbursements:', error);
      setRecentDisbursements([]);
      setApiError('Failed to load recent disbursements. Please try refreshing the page.');
    } finally {
      setIsDisbursementsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentDisbursements();
  }, [fetchRecentDisbursements]);

  useEffect(() => {
    if (formData.recipientType === 'individual' && !initialMembersLoaded) {
      loadInitialMembers();
    }
  }, [formData.recipientType, initialMembersLoaded, tenantId]);

  const loadInitialMembers = async () => {
    setMembersLoading(true);
    try {
      const effectiveTenantId = tenantId || localStorage.getItem('tenantId');
      if (!effectiveTenantId) return;

      const filters = { tenantId: effectiveTenantId };
      const response = await memberService.getAllMembers(1, 50, '', { ...filters, status: 'active' });

      let memberList = [];
      if (response && response.data && Array.isArray(response.data)) {
        memberList = response.data;
      } else if (response && Array.isArray(response)) {
        memberList = response;
      } else if (response && response.members && Array.isArray(response.members)) {
        memberList = response.members;
      } else {
        return;
      }

      const processedMembers = memberList.map((member) => {
        if (member.userId && typeof member.userId === 'object') {
          const firstName = member.userId.firstName || '';
          const lastName = member.userId.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            _id: member._id,
            name: fullName || 'Unnamed Member',
            email: member.userId.email || 'No email',
            status: member.status || 'unknown',
          };
        }

        const firstName = member.firstName || '';
        const lastName = member.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          _id: member._id,
          name: fullName || member.name || 'Unnamed Member',
          email: member.email || 'No email',
          status: member.status || 'unknown',
        };
      });

      setMemberOptions(processedMembers);
      setInitialMembersLoaded(true);
    } catch (error) {
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchMembers = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) return;

    setMembersLoading(true);
    try {
      const effectiveTenantId = tenantId || localStorage.getItem('tenantId');
      if (!effectiveTenantId) throw new Error('Tenant identification required');

      const filters = { tenantId: effectiveTenantId };
      const response = await memberService.getAllMembers(1, 20, searchQuery, filters);

      let memberList = [];
      if (response && response.data && Array.isArray(response.data)) {
        memberList = response.data;
      } else if (response && Array.isArray(response)) {
        memberList = response;
      } else if (response && response.members && Array.isArray(response.members)) {
        memberList = response.members;
      } else {
        return [];
      }

      const processedMembers = memberList.map((member) => {
        if (member.userId && typeof member.userId === 'object') {
          const firstName = member.userId.firstName || '';
          const lastName = member.userId.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            _id: member._id,
            name: fullName || 'Unnamed Member',
            email: member.userId.email || 'No email',
            status: member.status || 'unknown',
          };
        }

        const firstName = member.firstName || '';
        const lastName = member.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          _id: member._id,
          name: fullName || member.name || 'Unnamed Member',
          email: member.email || 'No email',
          status: member.status || 'unknown',
        };
      });

      const activeMembers = processedMembers.filter((m) => m.status !== 'inactive');
      setMemberOptions(activeMembers);
      return activeMembers;
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        memberSearch: 'Failed to search members: ' + error.message,
      }));
      setMemberOptions([]);
      return [];
    } finally {
      setMembersLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (memberSearch && memberSearch.length >= 2) {
        fetchMembers(memberSearch);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [memberSearch, fetchMembers]);

  useEffect(() => {
    const fetchTokenDetails = async () => {
      if (!tokenId) return;

      try {
        console.log('Fetching full token details');
        const response = await tenantTokenService.getTenantToken();
        if (response && response.success && response.data) {
          const tokenInfo = response.data;
          console.log('Received token details:', tokenInfo);
          setTokenData({
            _id: tokenInfo._id,
            symbol: tokenInfo.symbol || tokenSymbol || 'MBC',
            name: tokenInfo.name || 'MemberCoin',
          });
        }
      } catch (error) {
        console.error('Error fetching token details:', error);
      }
    };

    fetchTokenDetails();
  }, [tokenId, tokenSymbol]);

  const validateForm = () => {
    const newErrors = {};

    if (formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if (formData.recipientType === 'individual' && (!formData.recipients || formData.recipients.length === 0)) {
      newErrors.recipients = 'At least one recipient is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'recipientType' && value === 'individual' && !initialMembersLoaded) {
      loadInitialMembers();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) : value,
    }));
  };

  const handleRecipientsChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      recipients: newValue,
    }));
  };

  const formatRecipientsDisplay = (recipientType, successfulCount, recipients) => {
    if (recipientType === 'individual') {
      return `${recipients.length} members`;
    } else if (recipientType === 'all') {
      return 'All members';
    } else if (recipientType === 'active') {
      return 'Active members';
    }
    return `${successfulCount} members`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setSuccess(false);

      const disbursementData = {
        tokenId: tokenData._id,
        amount: formData.amount,
        reason: formData.reason || 'Not specified',
        notes: formData.notes,
        recipientType: formData.recipientType,
        recipients:
          formData.recipientType === 'individual'
            ? formData.recipients.map((r) => r._id)
            : formData.recipientType,
      };

      console.log('Submitting disbursement data:', disbursementData);

      const response = await tokenDistributionService.createDisbursement(disbursementData);

      console.log('Disbursement creation response:', response);

      if (!response || !response.success) {
        throw new Error(
          'Failed to create disbursement: ' +
            (response?.data?.message || 'Server returned an invalid response')
        );
      }

      setFormData({
        amount: 10,
        reason: '',
        notes: '',
        recipients: [],
        recipientType: 'individual',
      });
      setSuccess(true);

      if (onDisbursement) {
        onDisbursement();
      }

      const newDisbursement = {
        _id: response.data?._id || `new-${Date.now()}`,
        date: response.data?.createdAt || new Date().toISOString(),
        amount: disbursementData.amount,
        recipients: formatRecipientsDisplay(
          formData.recipientType, 
          response.data?.successful || 0, 
          formData.recipients
        ),
        reason: disbursementData.reason,
        createdBy: response.data?.createdBy?.name || 'Current Admin',
        status: response.data?.status || 'COMPLETED',
      };

      setRecentDisbursements((prev) => [newDisbursement, ...prev]);

      setTimeout(() => {
        fetchRecentDisbursements();
      }, 1000);
    } catch (error) {
      console.error('Error creating disbursement:', error);
      setErrors({
        submit: 'Failed to create disbursement: ' + (error.response?.data?.message || error.message),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" paragraph>
        Distribute tokens to members manually. You can select individual members or distribute to all members.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Create New Disbursement
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Tokens successfully distributed!
              </Alert>
            )}

            {errors.submit && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errors.submit}
              </Alert>
            )}

            {apiError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {apiError}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Recipient Type</InputLabel>
                    <Select
                      name="recipientType"
                      value={formData.recipientType}
                      onChange={handleChange}
                      label="Recipient Type"
                    >
                      <MenuItem value="individual">Individual Members</MenuItem>
                      <MenuItem value="active">All Active Members</MenuItem>
                      <MenuItem value="all">All Members</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.recipientType === 'individual' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      id="recipients"
                      options={memberOptions}
                      getOptionLabel={(option) => `${option.name} (${option.email})`}
                      loading={membersLoading}
                      value={formData.recipients}
                      onChange={handleRecipientsChange}
                      onInputChange={(event, newInputValue) => {
                        setMemberSearch(newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Recipients"
                          placeholder={membersLoading ? "Loading members..." : "Search members or select from the list..."}
                          error={!!errors.recipients}
                          helperText={errors.recipients || (memberOptions.length === 0 && !membersLoading ? "No members found. Try a different search." : "")}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {membersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography>{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            size="small"
                          />
                        ))
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="amount"
                    label={`Amount (${tokenData.symbol})`}
                    type="number"
                    value={formData.amount}
                    onChange={handleChange}
                    InputProps={{ inputProps: { min: 1 } }}
                    error={!!errors.amount}
                    helperText={errors.amount}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="reason"
                    label="Reason"
                    value={formData.reason}
                    onChange={handleChange}
                    error={!!errors.reason}
                    helperText={errors.reason}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="notes"
                    label="Notes (Optional)"
                    value={formData.notes}
                    onChange={handleChange}
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={24} /> : <Send />}
                  >
                    Distribute Tokens
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <History sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Recent Disbursements</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {isDisbursementsLoading && recentDisbursements.length === 0 ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : recentDisbursements.length === 0 ? (
              <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                No recent disbursements found
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Recipients</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentDisbursements.map((disbursement) => (
                      <TableRow key={disbursement._id}>
                        <TableCell>
                          {new Date(disbursement.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {disbursement.amount} {tokenData.symbol}
                        </TableCell>
                        <TableCell>{disbursement.recipients}</TableCell>
                        <TableCell>{disbursement.reason}</TableCell>
                        <TableCell>{disbursement.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TokenDisbursement;